'use client'

import { useEffect, useRef } from 'react';
import type { ClientResourceProviderProps } from '../interfaces/ClientResourceProviderProps';
import { configStack } from '../functions/internal/configStack';

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Client component that sets the global ResourceConfig so that getResource() works in client components</blockquote>
 * 
 * @example
 * ```tsx
 * <ClientResourceProvider config={myResourceConfig}>
 *   <App />
 * </ClientResourceProvider>
 * ```
 * 
 * @param config - The resource configuration to provide to child components
 */
export function ClientResourceProvider({ config, children }: ClientResourceProviderProps) {
  const pushedRef = useRef(false);
  
  // Push config synchronously during render so it's available immediately
  if (!pushedRef.current && typeof window !== 'undefined') {
    configStack.push(config);
    pushedRef.current = true;
  }
  
  useEffect(() => {
    // Cleanup: remove this config from stack on unmount
    return () => {
      if (typeof window !== 'undefined') {
        const index = configStack.indexOf(config);
        if (index !== -1) {
          configStack.splice(index, 1);
        }
      }
    };
  }, [config]);

  return <>{children}</>;
}

